/**
 * @fileoverview Civil & Criminal Offences — static per-head configuration
 * @module jobs/summarization/agents/civilCriminalOffences/headConfigs
 *
 * One config per managed offence head (MOR-672 sub-tickets), consumed by:
 *   - `headFactory.ts` — builds each head's ManagedAgentManifest
 *   - `debounce.ts` — maps headKey → manifestId when scheduling runs
 *   - `runner.ts` — OFFENCE_UPSTREAM drives sibling-agent reactivity
 *   - the workspace UI (labels and ordering via publicQueries)
 *
 * This family mirrors flaggedTransactions/headConfigs.ts. The key difference:
 * the offence heads are a *claim layer* — their upstream feeders include the
 * flagged-transactions heads themselves (`sum_flagged_*`), so a flagged head
 * completing re-arms the dependent offence heads.
 */

import type { OffenceHeadKey } from "@cvx/jobs/summarization/offenceFindings/schema";

export const OFFENCE_MASTER_MANIFEST_ID = "sum_offence_master";
export const OFFENCE_MASTER_AGENT_NAME = "Civil & Criminal Offences Master";

export interface OffenceHeadConfig {
  headKey: OffenceHeadKey;
  /** Manifest / summarizationTasks agentId. */
  manifestId: string;
  /** Full display name (master table row). */
  name: string;
  /** Compact label (chips, breadcrumbs). */
  shortLabel: string;
  /** Statutory hook shown alongside the name. */
  provision: string;
  /** Base name for the Anthropic agent env-var triple (`<base>`, `<base>_DEV`, `<base>_VERSION`). */
  envBase: string;
  activityLogPrefix: string;
  promptKey: string;
  /**
   * Whether this head carries a recovery / compensation measure. The pure
   * offence heads (s206A, s471A is recoverable, ROCAP, s530) are referral-only
   * and report `none` banding with no dollar total.
   */
  hasQuantum: boolean;
  /** Short blurb for the master table / workspace cards. */
  uiDescription: string;
}

/** Display order matches the sub-ticket grouping (duties → records → trading → phoenix → offences). */
export const OFFENCE_HEAD_ORDER: ReadonlyArray<OffenceHeadKey> = [
  "care_diligence",
  "good_faith",
  "improper_position",
  "improper_information",
  "books_records_failure",
  "insolvent_trading",
  "creditor_defeating_disposition",
  "avoiding_employee_entitlements",
  "disqualified_management",
  "post_appointment_dealings",
  "rocap_failure",
  "failure_to_assist",
];

export const OFFENCE_HEAD_CONFIGS: Record<OffenceHeadKey, OffenceHeadConfig> = {
  care_diligence: {
    headKey: "care_diligence",
    manifestId: "sum_offence_care_diligence",
    name: "Breach of Duty of Care & Diligence",
    shortLabel: "s180",
    provision: "s180",
    envBase: "ANTHROPIC_AGENT_OFFENCE_CARE_DILIGENCE",
    activityLogPrefix: "[OffenceCare]",
    promptKey: "managed.offence.careDiligence.system",
    hasQuantum: true,
    uiDescription:
      "Director's duty of care and diligence — failures to monitor solvency, maintain records, or oversee major decisions, framed per director against the conduct each caused.",
  },
  good_faith: {
    headKey: "good_faith",
    manifestId: "sum_offence_good_faith",
    name: "Breach of Good Faith / Proper Purpose",
    shortLabel: "s181",
    provision: "s181 / s184(1)",
    envBase: "ANTHROPIC_AGENT_OFFENCE_GOOD_FAITH",
    activityLogPrefix: "[OffenceGoodFaith]",
    promptKey: "managed.offence.goodFaith.system",
    hasQuantum: true,
    uiDescription:
      "Good faith in the best interests of the company and proper purpose — preferring insider, related-party or shareholder interests once solvency was in doubt.",
  },
  improper_position: {
    headKey: "improper_position",
    manifestId: "sum_offence_improper_position",
    name: "Improper Use of Position",
    shortLabel: "s182",
    provision: "s182 / s184(2)",
    envBase: "ANTHROPIC_AGENT_OFFENCE_IMPROPER_POSITION",
    activityLogPrefix: "[OffencePosition]",
    promptKey: "managed.offence.improperPosition.system",
    hasQuantum: true,
    uiDescription:
      "Use of position to gain an advantage or cause detriment — extractions, asset direction to related entities, security granted to insiders. Reaches officers and employees.",
  },
  improper_information: {
    headKey: "improper_information",
    manifestId: "sum_offence_improper_information",
    name: "Improper Use of Information",
    shortLabel: "s183",
    provision: "s183 / s184(3)",
    envBase: "ANTHROPIC_AGENT_OFFENCE_IMPROPER_INFORMATION",
    activityLogPrefix: "[OffenceInformation]",
    promptKey: "managed.offence.improperInformation.system",
    hasQuantum: true,
    uiDescription:
      "Use of information obtained by virtue of position — insider knowledge of insolvency timed to personal benefit, or client/contract data moved to a successor entity.",
  },
  books_records_failure: {
    headKey: "books_records_failure",
    manifestId: "sum_offence_books_records_failure",
    name: "Failure to Keep Records",
    shortLabel: "s286/s344",
    provision: "s286 / s344(1) / s344(2)",
    envBase: "ANTHROPIC_AGENT_OFFENCE_BOOKS_RECORDS",
    activityLogPrefix: "[OffenceBooks]",
    promptKey: "managed.offence.booksRecordsFailure.system",
    hasQuantum: false,
    uiDescription:
      "Director's failure to take reasonable steps to secure proper financial records — translates the Adequacy verdict, states the s588E(4) presumption, indirect quantum.",
  },
  insolvent_trading: {
    headKey: "insolvent_trading",
    manifestId: "sum_offence_insolvent_trading",
    name: "Insolvent Trading",
    shortLabel: "s588G",
    provision: "s588G / s588M",
    envBase: "ANTHROPIC_AGENT_OFFENCE_INSOLVENT_TRADING",
    activityLogPrefix: "[OffenceInsTrading]",
    promptKey: "managed.offence.insolventTrading.system",
    hasQuantum: true,
    uiDescription:
      "Claim layer over the flagged Insolvent Trading head — adopts the per-director exposure, adds contravention characterisation, s588H/s588GA defence development and the s588G(3) criminal overlay.",
  },
  creditor_defeating_disposition: {
    headKey: "creditor_defeating_disposition",
    manifestId: "sum_offence_creditor_defeating_disposition",
    name: "Creditor-Defeating Disposition",
    shortLabel: "s588FDB",
    provision: "s588FDB / s588GAB / s588GAC",
    envBase: "ANTHROPIC_AGENT_OFFENCE_CDD",
    activityLogPrefix: "[OffenceCDD]",
    promptKey: "managed.offence.creditorDefeatingDisposition.system",
    hasQuantum: true,
    uiDescription:
      "Officer and procurer claims over creditor-defeating dispositions — screens the flagged defeat-creditors items to the CDD conditions, frames the s588GAB officer and s588GAC procurer respondents.",
  },
  avoiding_employee_entitlements: {
    headKey: "avoiding_employee_entitlements",
    manifestId: "sum_offence_avoiding_employee_entitlements",
    name: "Avoiding Employee Entitlements",
    shortLabel: "s596AB",
    provision: "s596AB / s596AC",
    envBase: "ANTHROPIC_AGENT_OFFENCE_EMPLOYEE_ENTITLEMENTS",
    activityLogPrefix: "[OffenceEmployee]",
    promptKey: "managed.offence.avoidingEmployeeEntitlements.system",
    hasQuantum: true,
    uiDescription:
      "Claim layer over the flagged defeat-employee-entitlements items — sizes the entitlement shortfall from Preferred Creditors/Employment, attributes contraveners and accessories under s596AC.",
  },
  disqualified_management: {
    headKey: "disqualified_management",
    manifestId: "sum_offence_disqualified_management",
    name: "Disqualified Management",
    shortLabel: "s206A",
    provision: "s206A / s206B–G",
    envBase: "ANTHROPIC_AGENT_OFFENCE_DISQUALIFIED",
    activityLogPrefix: "[OffenceDisqual]",
    promptKey: "managed.offence.disqualifiedManagement.system",
    hasQuantum: false,
    uiDescription:
      "A disqualified person managing the company — overlaps management periods against disqualification grounds; also surfaces prospective two-failed-companies disqualification grounds. Referral only.",
  },
  post_appointment_dealings: {
    headKey: "post_appointment_dealings",
    manifestId: "sum_offence_post_appointment_dealings",
    name: "Post-Appointment Dealings",
    shortLabel: "s471A",
    provision: "s471A / s468",
    envBase: "ANTHROPIC_AGENT_OFFENCE_POST_APPOINTMENT",
    activityLogPrefix: "[OffencePostAppt]",
    promptKey: "managed.offence.postAppointmentDealings.system",
    hasQuantum: true,
    uiDescription:
      "Dealings on the company's pre-appointment account after the date of appointment — unauthorised exercise of suspended officer powers and void dispositions, recoverable from recipient and officer.",
  },
  rocap_failure: {
    headKey: "rocap_failure",
    manifestId: "sum_offence_rocap_failure",
    name: "Failing to Provide ROCAP",
    shortLabel: "ROCAP",
    provision: "s429 / s438B / s446C / s475 / s530A",
    envBase: "ANTHROPIC_AGENT_OFFENCE_ROCAP",
    activityLogPrefix: "[OffenceRocap]",
    promptKey: "managed.offence.rocapFailure.system",
    hasQuantum: false,
    uiDescription:
      "An officer's failure to provide the Report on Company Activities and Property, in full and verified, after the due date and request. Referral only.",
  },
  failure_to_assist: {
    headKey: "failure_to_assist",
    manifestId: "sum_offence_failure_to_assist",
    name: "Failure to Assist Liquidator",
    shortLabel: "s530A/B",
    provision: "s530A / s530B",
    envBase: "ANTHROPIC_AGENT_OFFENCE_FAILURE_TO_ASSIST",
    activityLogPrefix: "[OffenceAssist]",
    promptKey: "managed.offence.failureToAssist.system",
    hasQuantum: false,
    uiDescription:
      "Failure to deliver up books and property, attend, or give information (s530A), and books delivery-up by any holder (s530B). Three de-conflicted limbs. Referral only.",
  },
};

const HEAD_BY_MANIFEST_ID = new Map<string, OffenceHeadConfig>(
  Object.values(OFFENCE_HEAD_CONFIGS).map((c) => [c.manifestId, c]),
);

export function offenceHeadConfigForManifestId(
  manifestId: string,
): OffenceHeadConfig | undefined {
  return HEAD_BY_MANIFEST_ID.get(manifestId);
}

/** Reverse lookup: manifest id → head key (used by tools via toolCtx.agentId). */
export function offenceHeadKeyForManifestId(
  manifestId: string,
): OffenceHeadKey | undefined {
  return HEAD_BY_MANIFEST_ID.get(manifestId)?.headKey;
}

/**
 * Sibling-agent reactivity: upstream manifest id → offence heads to re-arm
 * when that agent completes a successful (non-chat) run. Consumed by
 * `runner.ts` alongside FLAGGED_UPSTREAM and BOOKS_AND_RECORDS_UPSTREAM.
 *
 * Derived from each sub-ticket's DATA TO REVIEW list. Note that the flagged
 * heads are themselves upstream feeders here — the offence heads are a claim
 * layer over the flagged output.
 */
export const OFFENCE_UPSTREAM: Record<string, ReadonlyArray<OffenceHeadKey>> = {
  // ---- Flagged-transactions heads (the conduct feed) ----
  sum_flagged_unfair_preferences: ["care_diligence", "good_faith"],
  sum_flagged_insolvent_trading: [
    "insolvent_trading",
    "care_diligence",
    "good_faith",
  ],
  sum_flagged_director_related: [
    "care_diligence",
    "good_faith",
    "improper_position",
    "improper_information",
    "creditor_defeating_disposition",
  ],
  sum_flagged_uncommercial: [
    "care_diligence",
    "good_faith",
    "creditor_defeating_disposition",
  ],
  sum_flagged_related_entity_uncommercial: [
    "care_diligence",
    "good_faith",
    "creditor_defeating_disposition",
  ],
  sum_flagged_unfair_loans: ["care_diligence", "improper_position"],
  sum_flagged_defeat_creditors: [
    "care_diligence",
    "good_faith",
    "improper_position",
    "improper_information",
    "creditor_defeating_disposition",
  ],
  sum_flagged_creditor_defeating: [
    "creditor_defeating_disposition",
    "care_diligence",
    "good_faith",
  ],
  sum_flagged_defeat_employees: [
    "avoiding_employee_entitlements",
    "care_diligence",
  ],
  // ---- Summary / review feeders ----
  sum_books_and_records: [
    "care_diligence",
    "good_faith",
    "books_records_failure",
    "insolvent_trading",
    "creditor_defeating_disposition",
    "rocap_failure",
    "failure_to_assist",
  ],
  sum_financial_analysis: [
    "care_diligence",
    "good_faith",
    "improper_position",
    "improper_information",
    "books_records_failure",
    "creditor_defeating_disposition",
    "avoiding_employee_entitlements",
  ],
  sum_corporate: [
    "care_diligence",
    "good_faith",
    "improper_position",
    "improper_information",
    "books_records_failure",
    "insolvent_trading",
    "creditor_defeating_disposition",
    "avoiding_employee_entitlements",
    "disqualified_management",
    "post_appointment_dealings",
    "rocap_failure",
    "failure_to_assist",
  ],
  sum_related_parties: [
    "care_diligence",
    "good_faith",
    "improper_information",
    "creditor_defeating_disposition",
    "avoiding_employee_entitlements",
    "disqualified_management",
  ],
  sum_board_reports: [
    "care_diligence",
    "good_faith",
    "improper_information",
    "books_records_failure",
    "insolvent_trading",
    "creditor_defeating_disposition",
    "avoiding_employee_entitlements",
  ],
  sum_bank_transactions: ["improper_position", "post_appointment_dealings"],
  sum_loans: ["care_diligence", "improper_position", "books_records_failure"],
  sum_asset_sales: [
    "care_diligence",
    "good_faith",
    "improper_position",
    "improper_information",
    "creditor_defeating_disposition",
    "avoiding_employee_entitlements",
    "post_appointment_dealings",
  ],
  sum_employment: ["avoiding_employee_entitlements"],
  sum_liabilities: [
    "care_diligence",
    "good_faith",
    "avoiding_employee_entitlements",
    "post_appointment_dealings",
  ],
  sum_ato_sro: ["care_diligence", "good_faith"],
  sum_security_interests: ["improper_position", "improper_information"],
};

/** Head keys that carry a recovery measure (used for UI banding vs referral). */
export const QUANTUM_HEADS: ReadonlyArray<OffenceHeadKey> =
  OFFENCE_HEAD_ORDER.filter((k) => OFFENCE_HEAD_CONFIGS[k].hasQuantum);
